import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Divider,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Add, CheckCircleOutline, Delete, Description, Edit, ErrorOutline, GetApp, MoreVert } from '@material-ui/icons';
import { insertIf } from '@roc/feature-utils';
import { IconButton } from '@material-ui/core';
import {
  Button,
  ConfirmDialog,
  FileUploadArea,
  StandardDialog,
  StatusDiv,
} from '@roc/ui';
import { Document } from '@roc/feature-types';
import { useIsUploadLocked } from '@roc/feature-general-tasks';
import { useStore } from '@roc/client-portal-shared/hooks';
import { formatCurrency } from '@roc/ui/utils';
import { LeaseYesNoChip } from './leaseYesNoChip';
import { EditLeaseModal } from './editLeaseModal';
import { PreviewLease } from './previewLease';
import { LeaseRow } from '../util/types';
import { LEASE_TYPE_LABELS } from '../store/leaseFormStore';

export interface LeaseModalProps {
  open: boolean;
  onClose: () => void;
  document?: Document;
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  dropzoneLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 15,
  },
  tableContainer: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeadCell: {
    color: theme.palette.text.secondary,
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    backgroundColor: '#FAFAFA',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  emptyStateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIcon: {
    color: '#C8C8C8',
    fontSize: 48,
    marginBottom: theme.spacing(1.5),
  },
  emptyText: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  closeButton: {
    border: '1px solid #D0D0D0',
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#FAFAFA',
    },
  },
  addButton: {
    textTransform: 'none',
    fontWeight: 600,
  },
  actionButton: {
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    padding: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
    backgroundColor: '#fff',
  },
  documentLink: {
    color: '#4A90D9',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: '#2C6FAC',
    },
  },
  progressBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '16px',
    borderRadius: 8,
    border: '1px solid',
  },
  progressBannerWarning: {
    backgroundColor: '#FFFBEB',
    borderColor: '#F59E0B',
    color: '#92400E',
  },
  progressBannerInfo: {
    backgroundColor: '#F0FDF4',
    borderColor: '#86EFAC',
    color: '#166534',
  },
  progressIcon: {
    flexShrink: 0,
    fontSize: '1.1rem',
    color: 'inherit',
  },
  progressLabel: {
    fontWeight: 700,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
  progressPercent: {
    fontWeight: 700,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
}));

const WarningLinearProgress = withStyles({
  root: { flex: 1, borderRadius: 4, height: 8, backgroundColor: '#FDE68A' },
  bar: { borderRadius: 4, backgroundColor: '#B45309' },
})(LinearProgress);

const InfoLinearProgress = withStyles({
  root: { flex: 1, borderRadius: 4, height: 8, backgroundColor: '#BBF7D0' },
  bar: { borderRadius: 4, backgroundColor: '#16A34A' },
})(LinearProgress);

export const LeaseModal = observer(
  ({ open, onClose, document }: LeaseModalProps) => {
    const classes = useStyles();

    const [editLeaseOpen, setEditLeaseOpen] = useState(false);
    const [editingLeaseId, setEditingLeaseId] = useState<number | null>(null);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [menuLeaseIndex, setMenuLeaseIndex] = useState<number | null>(null);
    const [previewingLease, setPreviewingLease] = useState<LeaseRow | null>(null);
    const [confirmingDeleteLeaseId, setConfirmingDeleteLeaseId] = useState<number | null>(null);

    const openMenu = (
      event: React.MouseEvent<HTMLButtonElement>,
      index: number
    ) => {
      setMenuAnchor(event.currentTarget);
      setMenuLeaseIndex(index);
    };

    const closeMenu = () => {
      setMenuAnchor(null);
      setMenuLeaseIndex(null);
    };

    const { leaseStore, globalStore } = useStore();
    const uploadLocked = useIsUploadLocked(document?.status);

    const COLUMNS = [
      { label: 'Tenant Name' },
      { label: 'Unit' },
      { label: 'Vacant', align: 'center' as const },
      { label: 'Lease Type' },
      { label: 'Monthly Rent' },
      { label: 'Market Rent' },
      { label: 'Start Date', align: 'center' as const },
      { label: 'End Date', align: 'center' as const },
      { label: 'Document' },
      ...insertIf(globalStore.isInternalPortal, [{ label: 'Actions' }]),
    ];
    const { leases } = leaseStore;

    useEffect(() => {
      if (open && document?.loanId && document?.taskId) {
        leaseStore.setContext(document.loanId, document.collateralId, document.taskId);
        leaseStore.getLeaseInfo(document.loanId, document.taskId);
      }
    }, [open, document?.loanId, document?.collateralId, document?.taskId]);

    const handleClose = () => {
      leaseStore.setFiles([]);
      onClose();
    };

    const handleSubmit = () => {
      leaseStore.uploadFiles(document?.loanId, document?.collateralId, document?.taskId);
    };

    const dialogContent = (
      <>
        {leaseStore.unitCount !== null && (() => {
          const isWarning = leases.length < leaseStore.unitCount;
          const pct = Math.min(100, Math.round((leases.length / leaseStore.unitCount) * 100));
          return (
            <Box
              mb={2}
              className={`${classes.progressBanner} ${isWarning ? classes.progressBannerWarning : classes.progressBannerInfo}`}
            >
              {isWarning
                ? <ErrorOutline className={classes.progressIcon} />
                : <CheckCircleOutline className={classes.progressIcon} />}
              <Typography className={classes.progressLabel}>
                {isWarning
                  ? `${leases.length} of ${leaseStore.unitCount} required leases added`
                  : `All ${leaseStore.unitCount} required leases added`}
              </Typography>
              {isWarning
                ? <WarningLinearProgress variant="determinate" value={pct} />
                : <InfoLinearProgress variant="determinate" value={pct} />}
              <Typography className={classes.progressPercent}>{`${pct}%`}</Typography>
            </Box>
          );
        })()}

        {!uploadLocked && (
          <>
            <Typography className={classes.dropzoneLabel}>
              Drop lease documents here, or click to browse
            </Typography>
            <FileUploadArea
              items={leaseStore.files}
              setItems={updater => {
                leaseStore.setFiles(updater(leaseStore.files));
                handleSubmit();
              }}
              multiple
            />
          </>
        )}

        <Divider className={classes.divider} />

        {leases.length > 0 && leaseStore.processingStatus && (
          <Box mb={1.5}>
            <StatusDiv
              status={leaseStore.processingStatus}
              text={
                leaseStore.processingStatus === 'FAILED' && leaseStore.errorMessage
                  ? leaseStore.errorMessage
                  : {
                      IN_PROGRESS: 'Processing - This may take a few seconds. Click Refresh to check for results.',
                      SUCCESS: 'Documents ready – all uploaded files have been processed',
                      FAILED: 'Failed to read the document(s) – needs manual processing',
                    }[leaseStore.processingStatus] ?? ''
              }
              onRefresh={() => {
                if (document?.loanId && document?.taskId) {
                  leaseStore.getLeaseInfo(document.loanId, document.taskId);
                }
              }}
            />
          </Box>
        )}

        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            Leases you've added
          </Typography>
          {globalStore.isInternalPortal && (
            <Button
              className={classes.addButton}
              variant="contained"
              color="primary"
              size="small"
              disabled={leaseStore.processingStatus === 'IN_PROGRESS'}
              startIcon={<Add />}
              onClick={() => {
                leaseStore.leaseFormStore.reset();
                setEditingLeaseId(null);
                setEditLeaseOpen(true);
              }}
              testId="add-lease-button"
            >
              Add Lease
            </Button>
          )}
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.label} className={classes.tableHeadCell} align={col.align ?? 'left'}>
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {leases.map((lease, index) => (
                <TableRow key={index}>
                  <TableCell>{lease.tenantName ?? '--'}</TableCell>
                  <TableCell>{lease.unitNumber ?? '--'}</TableCell>
                  <TableCell align="center">
                    <LeaseYesNoChip value={lease.vacant ? 'Yes' : 'No'} />
                  </TableCell>
                  <TableCell>
                    {lease.leaseType
                      ? LEASE_TYPE_LABELS[lease.leaseType] ?? lease.leaseType
                      : '--'}
                  </TableCell>
                  <TableCell>
                    {lease.monthlyRent != null
                      ? formatCurrency(lease.monthlyRent)
                      : '--'}
                  </TableCell>
                  <TableCell>
                    {lease.marketRent != null
                      ? formatCurrency(lease.marketRent)
                      : '--'}
                  </TableCell>
                  <TableCell align="center">{lease.startDate ?? '--'}</TableCell>
                  <TableCell align="center">{lease.endDate ?? '--'}</TableCell>
                  <TableCell>
                    {lease.originalFileName && (
                      <span
                        className={classes.documentLink}
                        onClick={() => setPreviewingLease(lease)}
                      >
                        {lease.originalFileName}
                      </span>
                    )}
                  </TableCell>
                  {globalStore.isInternalPortal && (
                    <TableCell>
                      <IconButton
                        size="medium"
                        className={classes.actionButton}
                        disabled={leaseStore.processingStatus === 'IN_PROGRESS'}
                        onClick={e => openMenu(e, index)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={menuLeaseIndex === index ? menuAnchor : null}
                        open={menuLeaseIndex === index && Boolean(menuAnchor)}
                        onClose={closeMenu}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            leaseStore.loadEditForm(lease);
                            setEditingLeaseId(lease.leaseId ?? null);
                            setEditLeaseOpen(true);
                            closeMenu();
                          }}
                        >
                          <ListItemIcon>
                            <Edit fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Edit" />
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setConfirmingDeleteLeaseId(lease.leaseId);
                            closeMenu();
                          }}
                        >
                          <ListItemIcon>
                            <Delete fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Delete" />
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {leases.length === 0 && (
            <Box className={classes.emptyStateBox} py={6}>
              <Description className={classes.emptyIcon} />
              <Typography className={classes.emptyText}>
                No records found yet. Upload a lease document above to get started.
              </Typography>
            </Box>
          )}
        </TableContainer>
      </>
    );

    const dialogActions = (
      <Button
        className={classes.closeButton}
        variant="outlined"
        onClick={handleClose}
        testId="lease-modal-close"
      >
        Close
      </Button>
    );

    return (
      <>
        <StandardDialog
          open={open}
          title="Lease Agreement"
          maxWidth="lg"
          fullWidth
          handleClose={handleClose}
          dialogContent={dialogContent}
          dialogActions={dialogActions}
          dialogContentclasses={{ root: classes.dialogContent }}
        />
        <EditLeaseModal
          open={editLeaseOpen}
          editingLeaseId={editingLeaseId}
          onClose={() => {
            setEditLeaseOpen(false);
            setEditingLeaseId(null);
          }}
        />
        <ConfirmDialog
          open={confirmingDeleteLeaseId !== null}
          title="Delete Lease"
          body="Are you sure you want to delete this lease? This action cannot be undone."
          confirmButtonText="Delete"
          cancelButtonText="Cancel"
          handleClose={() => setConfirmingDeleteLeaseId(null)}
          handleCancel={() => setConfirmingDeleteLeaseId(null)}
          handleConfirm={() => {
            leaseStore.deleteLease(confirmingDeleteLeaseId);
            setConfirmingDeleteLeaseId(null);
          }}
        />
        <StandardDialog
          open={previewingLease !== null}
          title={previewingLease?.originalFileName ?? 'Document Preview'}
          handleClose={() => setPreviewingLease(null)}
          maxWidth="md"
          dialogContent={
            previewingLease && <PreviewLease lease={previewingLease} />
          }
          dialogActions={
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetApp />}
              onClick={() =>
                previewingLease && leaseStore.downloadLease(previewingLease.leaseId)
              }
              testId="lease-modal-download"
            >
              Download
            </Button>
          }
        />
      </>
    );
  }
);
